import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Boxes, Check, FolderInput, Loader2, Search, X } from "lucide-react";
import type AutomationSummary from "@/features/automations/models/AutomationSummary";
import { useWorkspaces } from "../hooks/useWorkspaces";
import type { WorkspaceSummary } from "../models/Workspace";

export interface MoveToWorkspaceModalProps {
  automation: AutomationSummary;
  /** Recibe el workspace destino, o `null` para dejar la automatización sin agrupar. */
  onMoved: (workspace: WorkspaceSummary | null) => void;
  onClose: () => void;
}

/**
 * Archiva una automatización en un workspace desde la lista general, sin tener que entrar al
 * workspace primero. Una automatización vive en uno solo, así que elegir otro la **mueve**; la
 * opción «Sin agrupar» la saca del actual.
 *
 * Solo se ofrecen workspaces propios: el backend rechaza archivar en el de otro.
 */
export default function MoveToWorkspaceModal({
  automation,
  onMoved,
  onClose,
}: MoveToWorkspaceModalProps) {
  const { loading, getAll, assignWorkflow, removeWorkflow } = useWorkspaces();

  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [query, setQuery] = useState("");
  const [pendingId, setPendingId] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    const fetch = async () => {
      const result = await getAll();
      if (!ignore && result) setWorkspaces(result.filter((w) => w.isOwner));
    };
    fetch();
    return () => {
      ignore = true;
    };
  }, [getAll]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return workspaces;
    return workspaces.filter((w) => w.name.toLowerCase().includes(q));
  }, [workspaces, query]);

  const currentId = automation.workspaceId ?? null;

  const handlePick = async (workspace: WorkspaceSummary | null) => {
    const targetId = workspace?.id ?? null;
    if (targetId === currentId || pendingId) return;

    setPendingId(targetId ?? "__none__");
    try {
      if (workspace) await assignWorkflow(workspace.id, automation.workflowId);
      else if (currentId) await removeWorkflow(currentId, automation.workflowId);
      onMoved(workspace);
    } catch {
      // el toast de error lo dispara useAsync
    } finally {
      setPendingId(null);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/40 backdrop-blur-md"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          className="relative z-10 flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-3xl border border-zinc-200/50 bg-white/90 shadow-2xl backdrop-blur-2xl"
        >
          <div className="flex items-start gap-4 border-b border-black/5 p-6">
            <div className="shrink-0 rounded-2xl bg-accent/10 p-3">
              <FolderInput className="size-6 text-accent" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-lg font-bold text-foreground">Mover a un workspace</h3>
              <p className="mt-0.5 truncate text-sm text-foreground/50" title={automation.name}>
                {automation.name}
              </p>
            </div>
            <button
              onClick={onClose}
              title="Cerrar"
              className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-black/5 bg-white/50 text-foreground/40 transition-all hover:bg-white hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          </div>

          {workspaces.length > 6 && (
            <div className="border-b border-black/5 p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground/30" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar workspace…"
                  className="w-full rounded-xl border border-black/5 bg-white/80 py-2.5 pl-10 pr-4 text-sm text-foreground shadow-sm transition-all placeholder:text-foreground/30 focus:border-accent/30 focus:outline-none focus:ring-4 focus:ring-accent/5"
                />
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4">
            {loading && workspaces.length === 0 ? (
              <div className="flex justify-center py-10">
                <Loader2 className="size-5 animate-spin text-foreground/30" />
              </div>
            ) : workspaces.length === 0 ? (
              <p className="py-10 text-center text-sm text-foreground/40">
                Todavía no creaste ningún workspace.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {visible.map((workspace) => (
                  <li key={workspace.id}>
                    <button
                      onClick={() => void handlePick(workspace)}
                      disabled={pendingId !== null}
                      className={`flex w-full cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                        workspace.id === currentId
                          ? "border-accent/20 bg-accent/10"
                          : "border-black/5 bg-white/60 hover:border-accent/30 hover:bg-white"
                      }`}
                    >
                      <span
                        className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-accent text-white"
                        style={workspace.color ? { backgroundColor: workspace.color } : undefined}
                      >
                        {workspace.icon &&
                        workspace.icon.length <= 4 &&
                        !workspace.icon.includes("/") ? (
                          <span className="text-base leading-none">{workspace.icon}</span>
                        ) : (
                          <Boxes className="size-4" />
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-foreground/90">
                          {workspace.name}
                        </span>
                        <span className="block truncate text-[11px] text-foreground/40">
                          {workspace.workflowCount}{" "}
                          {workspace.workflowCount === 1 ? "automatización" : "automatizaciones"}
                        </span>
                      </span>
                      {pendingId === workspace.id ? (
                        <Loader2 className="size-4 shrink-0 animate-spin text-accent" />
                      ) : workspace.id === currentId ? (
                        <Check className="size-4 shrink-0 text-accent" />
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {currentId && (
              <button
                onClick={() => void handlePick(null)}
                disabled={pendingId !== null}
                className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-black/5 bg-white/60 px-3 py-2.5 text-sm font-semibold text-foreground/60 transition-all hover:bg-white hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {pendingId === "__none__" && <Loader2 className="size-4 animate-spin" />}
                Dejar sin agrupar
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
